/**
 * The table's sounds, synthesized.
 *
 * Everything here is built from oscillators and filtered noise at call time —
 * no sample files, no licences, no fetch. That is not only thrift: the app's
 * whole surface is bottle caps on plywood, cards on a bench, tiles on felt,
 * and short dry transients drawn to measure sit in that world better than any
 * stock "casino_win_3.mp3" would.
 *
 * Design rules, applied to every voice:
 *  - Short. The longest thing here is the todas sting at ~0.7s; a move is
 *    30-80ms. Sounds annotate the game, they do not perform it.
 *  - Quiet by construction. Everything passes through one master fader set by
 *    the player's preset (SFX_GAIN), and each voice peaks well under 1.0 into
 *    it, so even "malakas" cannot clip and nothing can startle somebody
 *    wearing earphones at 2am.
 *  - Pitch is jittered a few percent per play. The same tansan never lands
 *    twice the same way on a real table either, and exact repetition is what
 *    makes a synthesized sound read as a buzzer.
 */

import { readPrefs, type SfxLevel } from '../screens/lib/prefs.js';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

/**
 * What each preset means at the master fader. The steps are perceptual, not
 * linear: roughly 12dB apart, which is what makes each one feel like a
 * different room rather than a different number. `katamtaman` is the 0.5 the
 * palette was voiced at; `malakas` still cannot clip because every voice peaks
 * under 1.0 into it.
 */
export const SFX_GAIN: Record<SfxLevel, number> = {
  off: 0,
  mahina: 0.13,
  katamtaman: 0.5,
  malakas: 0.9,
};

/** Read live, so a settings tap applies to the very next sound. */
function level(): SfxLevel {
  return readPrefs().sfx;
}

/**
 * The context is created lazily and resumed on the first gesture, because the
 * autoplay policy forbids anything else: an AudioContext born before a user
 * gesture starts life suspended. `unlockSound()` is wired to pointerdown in
 * the app root; until it runs, plays are dropped silently rather than queued —
 * a sound for a thing that happened seconds ago is worse than none.
 */
function ac(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  if (!ctx) {
    ctx = new AudioContext();
    master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);
  }
  return ctx;
}

export function unlockSound(): void {
  const c = ac();
  if (c && c.state === 'suspended') void c.resume().catch(() => undefined);
}

/**
 * The context, for the voice channel. Voice shares the context (and with it
 * the gesture unlock) but NOT the sfx master fader — turning game sounds down
 * must not turn people down. People have their own switch: mute table talk.
 */
export function sharedContext(): AudioContext | null {
  return ac();
}

/** Jitter a value by ±pct. The table never repeats itself exactly. */
const j = (v: number, pct: number): number => v * (1 + (Math.random() * 2 - 1) * pct);

/* ── Voice building blocks ────────────────────────────────────────────────── */

function tone(
  c: AudioContext,
  at: number,
  freq: number,
  dur: number,
  peak: number,
  type: OscillatorType = 'sine',
  glideTo?: number,
): void {
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, at);
  if (glideTo !== undefined) o.frequency.exponentialRampToValueAtTime(glideTo, at + dur);
  g.gain.setValueAtTime(0, at);
  g.gain.linearRampToValueAtTime(peak, at + 0.004);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  o.connect(g);
  g.connect(master!);
  o.start(at);
  o.stop(at + dur + 0.02);
}

/**
 * A burst of filtered noise — the body of every impact. `fc` is the filter's
 * centre: high and narrow reads as a card edge, low and wide as wood.
 */
function thud(
  c: AudioContext,
  at: number,
  fc: number,
  q: number,
  dur: number,
  peak: number,
  type: BiquadFilterType = 'bandpass',
): void {
  const n = c.createBufferSource();
  const len = Math.ceil(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  n.buffer = buf;
  const f = c.createBiquadFilter();
  f.type = type;
  f.frequency.value = fc;
  f.Q.value = q;
  const g = c.createGain();
  g.gain.value = peak;
  n.connect(f);
  f.connect(g);
  g.connect(master!);
  n.start(at);
}

/* ── The palette ──────────────────────────────────────────────────────────── */

export type SfxName =
  /* The move itself, in each venue's own material. */
  | 'tansan' // a bottle cap pressed onto plywood
  | 'card' // a card flicked flat onto wood
  | 'tile' // a mahjong tile clacked onto felt-over-wood
  | 'standee' // a wooden standee slid a square
  /* Shared table life. */
  | 'turn' // your turn — one soft knock
  | 'clock' // the last seconds of your clock
  | 'seat' // somebody sat down
  | 'talk' // a table-talk line landed
  | 'claim' // a claim window opened (mahjong pong/chow)
  | 'capture' // material came off the board
  | 'win'
  | 'lose'
  | 'chips'; // the settle receipt counting out

const VOICES: Record<SfxName, (c: AudioContext, at: number) => void> = {
  /* One press: a snap of high noise over a short knuckle of wood. */
  tansan: (c, at) => {
    thud(c, at, j(2600, 0.06), 1.2, 0.05, 0.5);
    thud(c, at + 0.004, j(320, 0.05), 2.5, 0.07, 0.4);
  },
  /* Mostly edge, hardly any body — cards weigh nothing. */
  card: (c, at) => {
    thud(c, at, j(4200, 0.08), 0.8, 0.035, 0.4, 'highpass');
    thud(c, at + 0.01, j(1100, 0.06), 1.6, 0.045, 0.25);
  },
  /* The heaviest impact on any table here, and proud of it. */
  tile: (c, at) => {
    thud(c, at, j(1900, 0.05), 3.2, 0.06, 0.6);
    thud(c, at + 0.006, j(430, 0.05), 2.2, 0.09, 0.5);
    tone(c, at, j(1240, 0.04), 0.05, 0.1, 'triangle');
  },
  /* A slide then a settle — the standee walks, not drops. */
  standee: (c, at) => {
    thud(c, at, j(700, 0.06), 0.7, 0.09, 0.22, 'lowpass');
    thud(c, at + 0.08, j(360, 0.05), 2.4, 0.06, 0.4);
  },
  turn: (c, at) => {
    tone(c, at, j(660, 0.02), 0.14, 0.22, 'sine');
    tone(c, at, j(1320, 0.02), 0.07, 0.08, 'sine');
  },
  clock: (c, at) => {
    tone(c, at, 880, 0.05, 0.16, 'square');
  },
  seat: (c, at) => {
    thud(c, at, j(500, 0.05), 1.8, 0.09, 0.35);
    tone(c, at + 0.02, j(520, 0.03), 0.1, 0.1);
  },
  talk: (c, at) => {
    tone(c, at, j(920, 0.04), 0.06, 0.14, 'triangle', 1250);
  },
  claim: (c, at) => {
    tone(c, at, 740, 0.09, 0.16, 'triangle');
    tone(c, at + 0.09, 988, 0.12, 0.16, 'triangle');
  },
  capture: (c, at) => {
    thud(c, at, j(2200, 0.06), 1.4, 0.05, 0.45);
    thud(c, at + 0.045, j(300, 0.06), 2, 0.08, 0.45);
  },
  /* Rising major triad — short enough to be a smile, not a jackpot. */
  win: (c, at) => {
    const base = 523.25;
    for (const [i, r] of [1, 1.25, 1.5, 2].entries())
      tone(c, at + i * 0.09, base * r, 0.3, 0.16, 'triangle');
  },
  /* Two settling notes. A loss at this table is an "ay!", not a funeral. */
  lose: (c, at) => {
    tone(c, at, 392, 0.22, 0.14, 'triangle');
    tone(c, at + 0.16, 311, 0.34, 0.12, 'triangle');
  },
  chips: (c, at) => {
    for (let i = 0; i < 5; i++)
      thud(c, at + i * j(0.05, 0.3), j(3100, 0.12), 2.6, 0.04, 0.3);
  },
};

/**
 * Play one voice now. Silent when the player said no, before the first
 * gesture, or where Web Audio does not exist (tests) — never an error.
 */
export function sfx(name: SfxName): void {
  const lv = level();
  if (lv === 'off') return;
  const c = ac();
  if (!c || c.state !== 'running' || !master) return;
  try {
    // The fader moves with a short time constant rather than a jump: a step
    // change in gain under a playing tail is an audible click of its own.
    master.gain.setTargetAtTime(SFX_GAIN[lv], c.currentTime, 0.02);
    VOICES[name](c, c.currentTime);
  } catch {
    /* An AudioContext mid-teardown can throw; a lost click is not a problem. */
  }
}
